/* eslint-disable custom/no-restricted-translation-jsx-untranslated-children-text */
import React from 'react';

import { Typography } from '@mui/material';

import { useIntl } from '@kamernet/core/Intl';
import { Locale } from '@kamernet/core/Intl/types';

import styles from './Content.module.css';

interface ContentCityStudentHousingProps {
  cityName: string;
}

export const ContentCityStudentHousing = ({
  cityName,
}: ContentCityStudentHousingProps) => {
  const { locale } = useIntl();

  return locale === Locale.en ? (
    <React.Fragment>
      <Typography variant="h1" className={styles.title}>
        {`Student housing in ${cityName}, the Netherlands`}
      </Typography>
      <Typography variant="body1" className={styles.paragraph}>
        {`Searching for student housing in ${cityName}, the Netherlands? Look no
        further than Kamernet to find student accommodation in ${cityName} close to
        universities. The demand for student residence in ${cityName} is on the rise
        as more students are planning to study abroad. There are several
        universities that provide scholarships for international students in
        ${cityName}. If you have been accepted to study there, plan ahead and secure
        student housing nearby for convenience. Since there are several types of
        accommodation for international students in ${cityName} including on-campus
        dormitories and off-campus housing, Kamernet lets you search and compare
        multiple options to find your perfect match.`}
      </Typography>

      <Typography variant="h2" className={styles.title}>
        {`Student apartments for rent in ${cityName}`}
      </Typography>
      <Typography variant="body1" className={styles.paragraph}>
        {`Renting student apartments in ${cityName} offers students a sense of
        independence and privacy. If you are looking for apartments for rent in
        ${cityName} for students, then we have got you covered. With a variety of
        options such as single room apartments, 2-bedroom apartments and
        student-friendly apartments, we've got the accommodation you need within
        your budget. Student flats in ${cityName} often come equipped with basic
        amenities, ensuring a comfortable and convenient living experience for
        students.`}
      </Typography>

      <Typography variant="h2" className={styles.title}>
        {`Student rooms for rent in ${cityName}`}
      </Typography>
      <Typography variant="body1" className={styles.paragraph}>
        {`Renting student rooms in ${cityName} offers students a sense of community and
        friendship. Renting rooms in ${cityName} for students is always an excellent
        choice if they’re seeking more affordable and community-oriented housing
        options. Whether students prefer a single room or are open to sharing
        with others, there are plenty of student rooms for rent in ${cityName} to
        accommodate their preferences and budget. On Kamernet, there are plenty
        of rooms for rent in ${cityName} for students to choose from, that are
        cost-effective and provide a comfortable and inclusive living
        experience.`}
      </Typography>

      <Typography variant="h2" className={styles.title}>
        {`Studio flats for students in ${cityName}`}
      </Typography>
      <Typography variant="body1" className={styles.paragraph}>
        {`Renting a student studio in ${cityName} offers students a balance between
        independence and simplicity. With a variety of studio apartments in
        ${cityName} for students, ranging from cozy units to spacious studios,
        students can find the perfect space to call their own. These studios
        provide students with a dedicated living space to focus on their studies
        and personal pursuits while enjoying the convenience of on-site
        amenities. Whether students prefer a furnished or unfurnished studio,
        there are plenty of student studio apartments in ${cityName} offering comfort
        and convenience.`}
      </Typography>

      <Typography variant="h2" className={styles.title}>
        {`Student dormitories in ${cityName}`}
      </Typography>
      <Typography variant="body1" className={styles.paragraph}>
        {`For students seeking more budget-friendly accommodation, student dorms
        for rent in ${cityName} can be an ideal choice. Renting a student dorm in
        ${cityName} provides a good opportunity to live in a community of fellow
        students while keeping costs low. Whether you're looking for a social
        atmosphere or a quiet space to focus on your studies, student dorms in
        ${cityName} offer diverse options to suit different preferences and needs.`}
      </Typography>

      <Typography variant="h2" className={styles.title}>
        {`Affordable student housing in ${cityName}`}
      </Typography>
      <Typography variant="body1" className={styles.paragraph}>
        {`Finding cheap accommodation in ${cityName} for students can be a challenge,
        but it's not impossible. With Kamernet, securing affordable student
        housing in ${cityName} is possible and within reach. Most students prefer
        cheap rooms in ${cityName} as they offer budget-friendly living spaces while
        some students prefer cheap apartments for rent as they provide
        affordable accommodation with ample amenities. With the increasing cost
        of living in ${cityName} for students, the demand for cheap flats to rent in
        ${cityName} for students is on the rise. Using our search filter, students
        can now easily find cheap student housing in ${cityName} that fits their
        budget without compromising on quality or comfort.`}
      </Typography>

      <Typography variant="h2" className={styles.title}>
        Long-term and Short-term accommodation
      </Typography>
      <Typography variant="body1" className={styles.paragraph}>
        {`International students planning to study in ${cityName} often face the
        challenge of finding suitable accommodation based on their allowed
        length of stay. Short-term student accommodations cater to the needs of
        students attending shorter courses, internships or language programs.
        whereas long-term student accommodations are ideal for students having
        longer academic commitments such as degree programs. Whether it's for
        long term or short term, you can search for student accommodation on
        Kamernet depending on your length of stay.`}
      </Typography>

      <Typography variant="h3" className={styles.title}>
        {`How to find student housing in ${cityName}?`}
      </Typography>
      <Typography variant="body1" className={styles.paragraph}>
        {`Rental platforms like Kamernet are a reliable, easy and safe way to find
        apartments and rooms for students in ${cityName} free of scams or worries.`}
      </Typography>

      <Typography variant="h3" className={styles.title}>
        {`How to find housing as an international student in ${cityName}?`}
      </Typography>
      <Typography variant="body1" className={styles.paragraph}>
        {`On Kamernet.nl, International students can filter and compare student
        housing in ${cityName} to find the right one based on their budget and
        preferences.`}
      </Typography>

      <Typography variant="h3" className={styles.title}>
        {`How much does it cost to live in ${cityName} as a student?`}
      </Typography>
      <Typography variant="body1" className={styles.paragraph}>
        {`Average cost of living in ${cityName} for a student is €1000 - €1500 per
        month. This cost will cover food, housing, insurance, transportation and
        more.`}
      </Typography>

      <Typography variant="h3" className={styles.title}>
        {`Where do international students live in ${cityName}?`}
      </Typography>
      <Typography variant="body1" className={styles.paragraph}>
        {`Most students in ${cityName} live off-campus across the city in private
        accommodations, shared housing, and short-term rentals close to their
        campus and universities.`}
      </Typography>
    </React.Fragment>
  ) : (
    <React.Fragment>
      <Typography variant="h1" className={styles.title}>
        {`Studentenwoningen in ${cityName}, Nederland`}
      </Typography>
      <Typography variant="body1" className={styles.paragraph}>
        {`Op zoek naar een studentenwoning in ${cityName}, Nederland? Zoek niet verder
        dan Kamernet voor een studentenhuis ${cityName} in de buurt van
        universiteiten. De vraag naar studentenwoningen in ${cityName} stijgt omdat
        steeds meer studenten van plan zijn om in het buitenland te gaan
        studeren. Er zijn verschillende universiteiten die beurzen beschikbaar
        stellen voor internationale studenten in ${cityName}. Als je toegelaten bent
        om daar te studeren, plan dan vooruit en zorg dat je voor je eigen gemak
        een studentenwoning in de buurt hebt. Omdat er verschillende soorten
        woningen zijn voor internationale studenten in ${cityName}, waaronder
        studentenkamers op de campus en studentenkamers buiten de campus, kun je
        met Kamernet verschillende opties zoeken en vergelijken om je perfecte
        match te vinden.`}
      </Typography>

      <Typography variant="h2" className={styles.title}>
        {`Studentenappartementen te huur in ${cityName}`}
      </Typography>
      <Typography variant="body1" className={styles.paragraph}>
        {`Het huren van studentenappartementen in ${cityName} biedt studenten een
        gevoel van onafhankelijkheid en privacy. Als je op zoek bent naar
        studentenappartementen in ${cityName}, dan kun je bij ons terecht. Met
        verschillende opties zoals eenkamerappartementen, appartementen met 2
        slaapkamers en studentvriendelijke appartementen, hebben we de woningen
        die je nodig hebt binnen je budget. Studentenappartementen in ${cityName}
        zijn vaak uitgerust met basisvoorzieningen, zodat studenten comfortabel
        en gemakkelijk kunnen wonen.`}
      </Typography>

      <Typography variant="h2" className={styles.title}>
        {`Studentenkamers te huur in ${cityName}`}
      </Typography>
      <Typography variant="body1" className={styles.paragraph}>
        {`Het huren van studentenkamers in ${cityName} biedt studenten een gevoel van
        gemeenschap en vriendschap. Het huren van studentenkamers in ${cityName} is
        altijd een uitstekende keuze als ze op zoek zijn naar betaalbaardere en
        gemeenschapsgerichte woonopties. Of studenten nu de voorkeur geven aan
        een eenpersoonskamer of openstaan voor delen met anderen, er zijn genoeg
        studentenkamers te huur in ${cityName} om aan hun voorkeuren en budget te
        voldoen. Op Kamernet zijn er genoeg kamers te huur in ${cityName} voor
        studenten om uit te kiezen, die kosteneffectief zijn en een comfortabele
        en inclusieve leefervaring bieden.`}
      </Typography>

      <Typography variant="h2" className={styles.title}>
        {`Studio-appartementen voor studenten in ${cityName}`}
      </Typography>
      <Typography variant="body1" className={styles.paragraph}>
        {`Het huren van een studentenstudio in ${cityName} biedt studenten een balans
        tussen onafhankelijkheid en eenvoud. Met een ruim aanbod aan studio's in
        ${cityName} voor studenten, variërend van gezellige kamers tot ruime
        studio's, kunnen studenten de perfecte ruimte voor zichzelf vinden. Deze
        studio's bieden studenten een eigen leefruimte om zich te concentreren
        op hun studie en persoonlijke bezigheden, terwijl ze kunnen genieten van
        het gemak van voorzieningen op locatie. Of studenten nu de voorkeur
        geven aan een gemeubileerde of ongemeubileerde studio, er zijn genoeg
        studentenstudio's in ${cityName} die comfort en gemak bieden.`}
      </Typography>

      <Typography variant="h2" className={styles.title}>
        {`Studentenhuis in ${cityName}`}
      </Typography>
      <Typography variant="body1" className={styles.paragraph}>
        {`Voor studenten die op zoek zijn naar betaalbare woningen, kunnen
        studentenhuizen te huur in ${cityName} een ideale keuze zijn. Het huren van
        een studentenhuizen in ${cityName} biedt een goede gelegenheid om in een
        gemeenschap van medestudenten te leven, terwijl de kosten laag blijven.
        Of je nu op zoek bent naar een sociale sfeer of een rustige ruimte om je
        op je studie te concentreren, studentenhuizen in ${cityName} bieden diverse
        opties om aan verschillende voorkeuren en behoeften te voldoen.`}
      </Typography>

      <Typography variant="h2" className={styles.title}>
        {`Goedkope studentenwoningen in ${cityName}`}
      </Typography>
      <Typography variant="body1" className={styles.paragraph}>
        {`Het vinden van goedkope woningen in ${cityName} voor studenten kan een
        uitdaging zijn, maar het is niet onmogelijk. Met Kamernet is een
        betaalbare studentenwoning in ${cityName} mogelijk en binnen handbereik. De
        meeste studenten geven de voorkeur aan goedkope kamers in ${cityName} omdat
        ze budgetvriendelijke woonruimtes bieden, terwijl sommige studenten de
        voorkeur geven aan goedkope huurappartementen omdat ze betaalbare
        woningen met uitgebreide voorzieningen bieden. Met de stijgende kosten
        van levensonderhoud in ${cityName} voor studenten, stijgt de vraag naar
        goedkope appartementen te huur in ${cityName} voor studenten. Met behulp van
        onze zoekfilter kunnen studenten nu gemakkelijk goedkope
        studentenwoningen in ${cityName} vinden die binnen hun budget passen zonder
        in te leveren op kwaliteit of comfort.`}
      </Typography>

      <Typography variant="h2" className={styles.title}>
        Lange en korte termijn woningen
      </Typography>
      <Typography variant="body1" className={styles.paragraph}>
        {`Internationale studenten die van plan zijn om in ${cityName} te studeren,
        worden vaak geconfronteerd met de uitdaging om geschikte woningen te
        vinden op basis van hun toegestane verblijfsduur. Korte-termijn
        studentenwoningen voldoen aan de behoeften van studenten die kortere
        cursussen, stages of taalprogramma's volgen, terwijl lange-termijn
        studentenwoningen ideaal zijn voor studenten met langere academische
        verplichtingen zoals opleidingsprogramma's. Of het nu voor de lange of
        korte termijn is, je kunt op Kamernet zoeken naar studentenwoningen in
        ${cityName} afhankelijk van je verblijfsduur.`}
      </Typography>

      <Typography variant="h3" className={styles.title}>
        {`Hoe vind je studentenwoningen in ${cityName}?`}
      </Typography>
      <Typography variant="body1" className={styles.paragraph}>
        {`Verhuurplatforms zoals Kamernet zijn een betrouwbare, gemakkelijke en
        veilige manier om appartementen en kamers voor studenten in ${cityName} te
        vinden, vrij van oplichting of zorgen.`}
      </Typography>

      <Typography variant="h3" className={styles.title}>
        {`Hoe vind je woningen als internationale student in ${cityName}?`}
      </Typography>
      <Typography variant="body1" className={styles.paragraph}>
        {`Op Kamernet.nl kunnen internationale studenten studentenwoning in ${cityName}
        filteren en vergelijken om de juiste te vinden op basis van hun budget
        en voorkeuren.`}
      </Typography>

      <Typography variant="h3" className={styles.title}>
        {`Hoeveel kost het om als student in ${cityName} te wonen?`}
      </Typography>
      <Typography variant="body1" className={styles.paragraph}>
        {`De gemiddelde kosten van levensonderhoud in ${cityName} voor een student
        bedragen €1000 - €1500 per maand. Deze kosten omvatten voedsel, woning,
        verzekering, transport en meer.`}
      </Typography>

      <Typography variant="h3" className={styles.title}>
        {`Waar wonen internationale studenten in ${cityName}?`}
      </Typography>
      <Typography variant="body1" className={styles.paragraph}>
        {`De meeste studenten in ${cityName} wonen buiten de campus in privé-kamers,
        gedeelde woning en kortetermijnverhuur verspreid over de stad, dicht bij
        hun campus en universiteiten.`}
      </Typography>
    </React.Fragment>
  );
};
